/**
 * Settings — pricing rules, category margins, the acting role, and two session preferences
 * (**My mailbox**, **Operating company**).
 *
 * Everything here reads and writes the LIVE server except the two session preferences and the role
 * switcher, each of which says on screen why it is local. Pricing rules and margins go through
 * `settings.get_pricing_rules` / `save_pricing_rules` (live-verified 2026-08-11).
 *
 * ---- Operating company ----
 *
 * Bosun runs seven legal entities across four currencies (`lookups.get_companies`), and every
 * enquiry must be raised under one — it is inherited by the RFQs, the supplier quotations, the
 * comparison and the customer quotation. `CreateEnquiryBar` requires a purchaser to pick one per
 * enquiry; this control sets the DEFAULT that pre-fills it, so someone working a day's mail for one
 * entity picks once instead of once per enquiry. Both write the same in-memory value
 * (`lib/api/company.ts`'s `setCompany`).
 *
 * It is a default, never an override: the create bar still shows the company and still lets it be
 * changed, because the wrong entity on a supplier-facing document is invisible after the fact.
 *
 * Since 2026-09-16 it is PERSISTED per user — on the Clerk user's metadata, via the persister the
 * identity bridge registers (`company.ts`) — so a reload no longer loses it (Akhil's report). There
 * is still no server field: `Bosun Settings.company` was withdrawn when the Frappe team told us
 * Bosun is multi-company, and the proxy runs every Frappe call as one service account, so a Frappe
 * user-default would be shared by everyone. **My mailbox** below persists the same way since
 * 2026-09-18 (`lib/api/mailbox.ts`).
 *
 * Why that one is real and here: enquiries arrive when a salesman forwards a customer's mail to a
 * single shared mailbox with the responsible purchaser on Cc. Several purchasers and salesmen use
 * that one mailbox, so without a filter every purchaser scrolls past everyone else's enquiries.
 * Setting an address here makes `/mail` default to the threads that address is on.
 *
 * The value comes from the `User` master rather than a free-text box: a Frappe `User`'s docname IS
 * the email address, so picking one cannot produce a typo that silently matches nothing.
 */

import { Link } from 'react-router-dom'
import { Card, PageHead } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { ROLE_LABELS, useSession } from '../../components/SessionProvider'
import { PricingRulesCard } from './PricingRulesCard'
import { CategoryMarginsCard } from './CategoryMarginsCard'
import { ROLES, type Role } from '../../lib/types'
import { usePurchaserPicker } from '../../lib/api/useEnquiryFromMail'
import { useCompanies, useCompanyPicker, useCurrentUser } from '../../lib/api/useMasters'
import { setCompany, useSessionCompany } from '../../lib/api/company'
import { useEffect, useMemo } from 'react'
import { actableRoles } from '../../lib/actingRole'

export function SettingsPage() {
  const { myAddress, setMyAddress, isManager, role, grantedRole, setRole } = useSession()

  // `company.ts` holds the value (it is read from non-React mutation code); this subscribes to
  // it, so the company restored from the user's profile after a refresh shows here too.
  const company = useSessionCompany() ?? ''
  const companies = useCompanies()
  const chosen = companies.data?.find((c) => c.name === company)

  /*
    The user's OWN companies, from the login profile (Akhil, 2026-09-19: "the login API carries
    a company column per purchaser — use it to populate the Settings"). The picker offers only
    these; when the profile names exactly one and nothing is set yet, it is set without a click.
    With several, the choice is still the purchaser's — nothing is guessed onto supplier-facing
    documents — but the list is theirs, not the whole master.
  */
  const currentUser = useCurrentUser()
  const mine = useMemo(() => currentUser.data?.companies ?? [], [currentUser.data])
  useEffect(() => {
    if (company === '' && mine.length === 1) setCompany(mine[0])
  }, [company, mine])
  const useMyCompanyPicker = (search: string) => {
    const all = useCompanyPicker(search)
    if (mine.length === 0) return all
    const allowed = new Set(mine)
    return { data: all.data?.filter((o) => allowed.has(o.name)), isPending: all.isPending }
  }

  return (
    <>
      <PageHead help="settings" title="Settings" subtitle="Pricing rules, category margins, mailboxes, and the acting role." />

      <Card style={{ marginBottom: 14 }}>
        <b>Operating company</b>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
          Every enquiry is raised under one of your {companies.data?.length ?? 'seven'} companies, and the RFQs,
          supplier quotations and customer quotation all inherit it. Set the one you are working for and{' '}
          <b>Create enquiry from this mail</b> will start there. You can still change it per enquiry.
        </div>

        <div className="fieldrow">
          <label htmlFor="operating-company">Company</label>
          <LinkCombo
            id="operating-company"
            value={company}
            onChange={(next) => setCompany(next)}
            useOptions={useMyCompanyPicker}
            placeholder={mine.length > 0 ? `Search your ${mine.length} compan${mine.length === 1 ? 'y' : 'ies'}…` : 'Search companies…'}
          />
        </div>
        {mine.length > 0 ? (
          <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--muted)' }}>
            Your profile allows {mine.length === 1 ? <b>{mine[0]}</b> : <>{mine.length} companies</>}
            {mine.length === 1 ? ' — set for you.' : ' — only those are offered here.'}
          </div>
        ) : null}

        {chosen ? (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            New enquiries default to <b>{chosen.name}</b>
            {chosen.default_currency ? (
              <>
                , which keeps its books in <b>{chosen.default_currency}</b>
              </>
            ) : null}
            .
          </div>
        ) : null}

        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--muted)' }}>
          A <b>default, not a lock</b> — the create bar shows the company and lets it be changed, because an RFQ sent
          under the wrong entity looks exactly like a correct one. It is <b>remembered on your user profile</b>, so
          it is still set after a reload and on another device (in a local dev build without sign-in it lasts
          the session only).
        </div>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <b>My mailbox</b>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
          Enquiries come into one shared mailbox, with the responsible purchaser on <b>Cc</b>. Set your address and{' '}
          <b>Mail</b> will show the threads you are on instead of everyone's. You can still switch back to{' '}
          <i>Addressed to anyone</i> there at any time.
        </div>

        <div className="fieldrow">
          <label htmlFor="my-address">My address</label>
          <LinkCombo
            id="my-address"
            value={myAddress}
            onChange={setMyAddress}
            useOptions={usePurchaserPicker}
            placeholder="Search users by name…"
          />
        </div>

        {myAddress ? (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            Mail now defaults to messages addressed to <b>{myAddress}</b>.{' '}
            <button type="button" className="linkish" onClick={() => setMyAddress('')}>
              clear
            </button>
          </div>
        ) : null}

        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--muted)' }}>
          {/*
            Two caveats stated on screen rather than buried in a comment, because both change what a
            user should expect from this control.
          */}
          This is a <b>view preference, not a permission</b> — the mailbox itself is shared, and anyone can switch the
          filter off. It is <b>saved to your sign-in</b> (not to this browser), so it follows you across reloads and
          devices.
        </div>
      </Card>

      {isManager && (
        <Card style={{ marginBottom: 14 }}>
          <b>Users</b>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
            Add colleagues, change what they may do, or revoke access. Signing in with Clerk is not
            enough on its own — a person needs a Waves user record here before they can do
            anything.
          </div>
          <Link to="/settings/users" className="btn">
            Manage users
          </Link>
        </Card>
      )}

      <PricingRulesCard />
      <CategoryMarginsCard />

      <Card style={{ marginBottom: 14 }}>
        <b>Role &amp; access</b>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, margin: '6px 0 10px' }}>
          Sales sees their own customers · Purchaser sees assigned requirements · Purchase Manager
          sees everything, plus analytics and user management.
        </div>

        <div className="fieldrow">
          <label htmlFor="acting-role">My role</label>
          <select
            id="acting-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            style={{ maxWidth: 260 }}
          >
            {ROLES.map((r) => (
              <option key={r} value={r} disabled={!actableRoles(grantedRole, ROLES).includes(r)}>
                {ROLE_LABELS[r]}{grantedRole === r ? ' (your role)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--muted)' }}>
          {/* Acting DOWN only (2026-09-21): a manager can look at the purchaser's board — scoped
              to their own enquiries — but nobody can pick a role above the one their account holds. */}
          Your account is <b>{grantedRole ? ROLE_LABELS[grantedRole] : 'still being looked up'}</b>. You can
          look at the app as a lower role — a purchaser's board shows only the requirements assigned
          to you, a purchaser's mailbox only the threads you are on — but not a higher one. Resets on reload.
        </div>
      </Card>
    </>
  )
}
